import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const location = useLocation(); // Hook to detect route changes
  let servicesTimeout: NodeJS.Timeout;
  let productsTimeout: NodeJS.Timeout;

  // Check if the current URL contains "/services"
  const isServicesActive = location.pathname.startsWith('/services');

  // Check if the current URL contains "/products"
  const isProductsActive = location.pathname.startsWith('/products');

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu and submenu on route change
  useEffect(() => {
    setIsMenuOpen(false); // Close the main menu
    setIsServicesOpen(false); // Close the Services submenu
    setIsProductsOpen(false); // Close the Products submenu
  }, [location]);

  // Open Services submenu
  const handleServicesEnter = () => {
    clearTimeout(servicesTimeout); // Clear any existing timeout
    setIsServicesOpen(true);
  };

  // Close Services submenu with a slight delay
  const handleServicesLeave = () => {
    servicesTimeout = setTimeout(() => {
      setIsServicesOpen(false);
    }, 200);
  };

  // Open Products submenu
  const handleProductsEnter = () => {
    clearTimeout(productsTimeout); // Clear any existing timeout
    setIsProductsOpen(true);
  };

  // Close Products submenu with a slight delay
  const handleProductsLeave = () => {
    productsTimeout = setTimeout(() => {
      setIsProductsOpen(false);
    }, 200);
  };

  return (
    <header
      className={`fixed w-full top-0 z-50 transition-all duration-300 ${
        isScrolled || isMenuOpen ? 'bg-white/80 backdrop-blur-lg shadow-lg' : 'bg-transparent'
      }`}
    >
      <nav
        className={`container mx-auto px-6 ${
          isScrolled || isMenuOpen ? 'py-3' : 'py-4'
        }`}
      >
        <div className="flex justify-between items-center">
          {/* Logo */}
          <div className="flex items-center">
            <NavLink to="/">
              <img
                src="/logo.png"
                alt="Hither Tech Logo"
                className="h-10 sm:h-12 transition-transform hover:scale-105"
              />
            </NavLink>
            <NavLink to="/">
              <span
                className={`ml-2 text-2xl font-bold bg-clip-text text-transparent 
                bg-gradient-to-r from-blue-600 to-purple-600 hover:scale-105 transition-transform`}
              >
                Hither Tech
              </span>
            </NavLink>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            {['Home', 'About', 'Careers', 'Contact'].map((item) => (
              <React.Fragment key={item}>
                <NavLink
                  to={item === 'Home' ? '/' : `/${item.toLowerCase()}`}
                  className={({ isActive }) =>
                    `group relative transition-colors ${
                      isActive
                        ? 'bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600 font-bold border-b-2 border-blue-600'
                        : isScrolled || isMenuOpen
                        ? 'text-gray-800 hover:text-blue-600 hover:border-b-2 hover:border-blue-600 font-medium'
                        : 'text-gray-300 hover:text-blue-600 hover:border-b-2 hover:border-blue-600 font-medium'
                    }`
                  }
                >
                  {item}
                </NavLink>
                
                {item === 'Home' && (
                  <div
                    className="relative"
                    onMouseEnter={handleServicesEnter} // Open submenu on hover
                    onMouseLeave={handleServicesLeave} // Close submenu on leave
                  >
                    <button
                      className={`${
                        isServicesActive
                          ? 'bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600 font-bold border-b-2 border-blue-600'
                          : isScrolled || isMenuOpen
                          ? 'text-gray-800 hover:text-blue-600 hover:border-b-2 hover:border-blue-600 font-medium'
                          : 'text-gray-300 hover:text-blue-600 hover:border-b-2 hover:border-blue-600 font-medium'
                      }`}
                    >
                      Services
                    </button>
                    {isServicesOpen && (
                      <div className="absolute left-1/2 transform -translate-x-1/2 bg-white shadow-lg rounded-lg mt-3 w-[350px] z-50 p-6">
                        {/* Submenu for Services */}
                        <div className="mb-2 flex gap-4">
                          <NavLink
                            to="/services"
                            className="px-4 py-2 rounded bg-blue-50 text-blue-700 font-semibold hover:bg-blue-100 transition"
                          >
                            View All Services
                          </NavLink>
                          <NavLink
                            to="/packages"
                            className="px-4 py-2 rounded bg-purple-50 text-purple-700 font-semibold hover:bg-purple-100 transition"
                          >
                            Our Packages
                          </NavLink>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Add PRODUCTS menu after Services */}
                {item === 'Home' && (
                  <div
                    className="relative"
                    onMouseEnter={handleProductsEnter} // Open submenu on hover
                    onMouseLeave={handleProductsLeave} // Close submenu on leave
                  >
                    <button
                      className={`${
                        isProductsActive
                          ? 'bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600 font-bold border-b-2 border-blue-600'
                          : isScrolled || isMenuOpen
                          ? 'text-gray-800 hover:text-blue-600 hover:border-b-2 hover:border-blue-600 font-medium'
                          : 'text-gray-300 hover:text-blue-600 hover:border-b-2 hover:border-blue-600 font-medium'
                      }`}
                    >
                      Products
                    </button>
                    {isProductsOpen && (
                      <div className="absolute left-1/2 transform -translate-x-1/2 bg-white shadow-lg rounded-lg mt-3 w-[600px] z-50 p-6">
                        <div className="grid grid-cols-2 gap-4">
                          {[
                            {
                              name: 'E-commerce Platform',
                              link: '/products/ecommerce-platform',
                              description: 'Build and manage your online store.',
                            },
                            {
                              name: 'Shopping Cart System',
                              link: '/products/shopping-cart-system',
                              description: 'Seamless shopping cart experience.',
                            },
                            {
                              name: 'Point of Sales (POS)',
                              link: '/products/pos',
                              description: 'Efficient POS system for retail.',
                            },
                            {
                              name: 'Stock Management System',
                              link: '/products/stock-management',
                              description: 'Track and manage inventory.',
                            },
                            {
                              name: 'Health Care Management System',
                              link: '/products/healthcare-management',
                              description: 'Manage healthcare operations.',
                            },
                            {
                              name: 'Warehouse Management System',
                              link: '/products/warehouse-management',
                              description: 'Optimize warehouse operations.',
                            },
                            {
                              name: 'HR Management System',
                              link: '/products/hr-management',
                              description: 'Streamline HR processes.',
                            },
                            {
                              name: 'Library Management System',
                              link: '/products/library-management',
                              description: 'Efficient Library Workflow.',
                            }
                          ].map((product) => (
                            <NavLink
                              key={product.name}
                              to={product.link}
                              className="block p-4 border rounded-lg hover:bg-blue-50 transition-all duration-200"
                            >
                              <h3 className="text-lg font-semibold text-gray-800 hover:text-blue-600">
                                {product.name}
                              </h3>
                              <p className="text-sm text-gray-600">{product.description}</p>
                            </NavLink>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </React.Fragment>
            ))}
            {/* Quote Button - Desktop */}
            <NavLink
              to="/quote"
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 
                rounded-lg font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300"
            >
              Request a Quote
            </NavLink>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <svg className="w-6 h-6" fill="none" stroke={`${(isScrolled || isMenuOpen) ? 'currentColor' : 'white'}`} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden mt-4 space-y-4">
            {['Home', 'Services', 'Packages', 'About', 'Careers', 'Contact'].map((item) => (
              <React.Fragment key={item}>
                <NavLink
                  to={item === 'Home' ? '/' : `/${item.toLowerCase()}`}
                  className={({ isActive }) =>
                    `block transition ${
                      isActive
                        ? 'text-blue-600 font-bold'
                        : (isScrolled || isMenuOpen)
                        ? 'text-gray-800 hover:text-blue-600'
                        : 'text-gray-600 hover:text-blue-600'
                    }`
                  }
                  onClick={() => setIsMenuOpen(false)} // Close menu on click
                >
                  {item}
                </NavLink>

                {/* Add PRODUCTS submenu after Packages */}
                {item === 'Packages' && (
                  <div>
                    <button
                      className="text-gray-800 hover:text-blue-600 font-medium transition-colors"
                      onClick={() => setIsProductsOpen(!isProductsOpen)} // Toggle submenu visibility
                    >
                      Products
                    </button>
                    {isProductsOpen && (
                      <div className="mt-2 ml-4 space-y-2">
                        {[
                          { name: 'E-commerce Platform', link: '/products/ecommerce-platform' },
                          { name: 'Shopping Cart System', link: '/products/shopping-cart-system' },
                          { name: 'Point of Sales (POS)', link: '/products/pos' },
                          { name: 'Stock Management System', link: '/products/stock-management' },
                          { name: 'HR Solutions', link: '/products/hr-management' },
                          { name: 'Health Care Management Systems', link: '/products/healthcare-management' },
                          { name: 'Warehouse Management Solution', link: '/products/warehouse-management' },
                        ].map((product) => (
                          <NavLink
                            key={product.name}
                            to={product.link}
                            className="block text-gray-600 hover:text-blue-600 transition-colors"
                            onClick={() => setIsMenuOpen(false)} // Close menu on click
                          >
                            {product.name}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </React.Fragment>
            ))}
            {/* Quote Button - Mobile */}
            <NavLink
              to="/quote"
              className="block bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 
                rounded-lg font-semibold text-center hover:shadow-lg transition-all duration-300"
              onClick={() => setIsMenuOpen(false)} // Close menu on click
            >
              Request a Quote
            </NavLink>
          </div>
        )}

      </nav>
    </header>
  );
};

export default Header;